/**
 * 66. MRP Planner — suggestions; exceptions; planned orders; release
 * to PO/Production.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatQuantity, formatDate } from "@/lib/money";

interface MrpRecommendationRow {
  id: string;
  runId: string;
  productSku: string;
  productName: string;
  demandType: "SalesOrder" | "ProductionOrder" | "Forecast" | "MinMax";
  demandRef: string;
  requiredDate: string | null;
  availableQty: string;
  suggestedOrderQty: string;
  supplyType: "PurchaseOrder" | "ProductionOrder" | "Transfer";
  status: "Planned" | "Released" | "Completed" | "Exception";
  exception?: string;
}

export function MrpPlannerPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<MrpRecommendationRow>(
    ["manufacturing", "mrp", "runs"],
    "/manufacturing/mrp/runs",
  );

  return (
    <div>
      <PageHeader
        title="MRP Planner"
        subtitle="Material requirements suggestions, exceptions, and planned orders."
        actions={
          <PendingButton variant="primary" reason="mrp" slice="MF-B8">
            {t("documentActions.runMrp")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`MRP recommendations (${data.total})`}>
          <DataTable<MrpRecommendationRow>
            rows={data.items}
            columns={[
              {
                key: "productSku",
                label: "Product",
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "demandType", label: "Demand type" },
              {
                key: "demandRef",
                label: "Demand ref",
                render: (r) => (
                  <span className="atlas-mono">{r.demandRef}</span>
                ),
              },
              {
                key: "requiredDate",
                label: "Required",
                render: (r) =>
                  r.requiredDate ? formatDate(r.requiredDate) : "—",
              },
              {
                key: "availableQty",
                label: "Available",
                numeric: true,
                render: (r) => formatQuantity(r.availableQty),
              },
              {
                key: "suggestedOrderQty",
                label: "Suggested order",
                numeric: true,
                render: (r) => (
                  <strong>{formatQuantity(r.suggestedOrderQty)}</strong>
                ),
              },
              { key: "supplyType", label: "Supply type" },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Exception" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "Planned" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <StatusBadge status={r.status} />
                  ),
              },
              {
                key: "exception",
                label: "Exception",
                render: (r) => r.exception ?? "—",
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
